import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Space, Spin, App } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import './TransportRouteFormPage.css';
import { omitUnchangedSharedTransportAllocationFields } from '../components/sharedTransportFormUtils';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import { normalizeTransportRouteDetail, type TransportRouteDetailDto } from '../dto/transport';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { mapTransportMutationErrorToMessage } from '../modules/transports/transportRouteViews';
import api from '../services/api';
import { authService } from '../services/auth';

const EditTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const orderIdToLink = searchParams.get('orderId');
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  const [transport, setTransport] = useState<TransportRouteDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();

  const fetchTransport = useCallback(async (transportId: string) => {
    try {
      const response = await api.get(`/transport-routes/${transportId}`);
      setTransport(normalizeTransportRouteDetail(response.data, 'transport.edit'));
    } catch (error) {
      message.error(t('transport:errorFetchingRoute'));
      navigate('/app/transports');
    }
  }, [navigate, t]);

  const fetchOrders = useCallback(async () => {
    try {
      // Ask for supplier purchase-value hints so newly-added cargo lines can
      // prefill "Wartość towaru" from their linked supplier orders. The backend
      // strips the hints for users without supplier-order purchase-price
      // visibility, so requesting them unconditionally is safe.
      const response = await api.get('/orders', {
        params: { includeSupplierPurchaseHints: 'true' },
      });
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.edit.availableOrders'));
    } catch (error) {
      message.error(t('transport:errorFetchingOrders'));
    } finally {
      setFetching(false);
    }
  }, [t]);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (!canManageTransports) {
      navigate('/app/transports', { replace: true });
      return;
    }

    void Promise.all([fetchTransport(id), fetchOrders()]);
  }, [canManageTransports, fetchOrders, fetchTransport, id, navigate]);

  const handleSubmit = async (payload: Record<string, unknown>) => {
    if (!id) {
      return;
    }

    setLoading(true);

    try {
      const updatePayload = transport
        ? omitUnchangedSharedTransportAllocationFields(payload, transport)
        : payload;
      await api.put(`/transport-routes/${id}`, updatePayload);
      message.success(t('transport:routeUpdatedSuccessfully'));
      navigate(`/app/transports/${id}`);
    } catch (error: any) {
      // MED (i18n error mapping): translate stable backend codes into
      // Polish-first copy — single home in
      // transportRouteViews.mapTransportMutationErrorToMessage.
      const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
      message.error(mapped || error?.response?.data?.error || t('transport:errorUpdatingRoute'));
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports) {
    return null;
  }

  if (fetching || !transport) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="transport-route-form-page">
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/app/transports/${id}`)}>
          {t('common:back')}
        </Button>
      </Space>

      <Card className="transport-route-form-shell-card" title={t('transport:editRoute')}>
        <SharedTransportForm
          availableOrders={availableOrders}
          transport={transport}
          initialOrderIds={orderIdToLink ? [orderIdToLink] : undefined}
          canEditTransportCosts={canEditTransportCosts}
          canViewTransportCosts={canViewTransportCosts}
          loading={loading}
          onCancel={() => navigate(`/app/transports/${id}`)}
          onSubmit={handleSubmit}
        />
      </Card>
    </div>
  );
};

export default EditTransportRoute;
